import { useMemo } from "react";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useWorkspaceId } from "../bridges/session";
import { api } from "../data/client";
import { scopedKey } from "../data/keys";
import type { AccessRole } from "../access/roleLabel";

/* ---------- URLs: all in one place, so a later task can adjust them ---------- */

const BASE = "/api/auth/next";
export const TEAM_URLS = {
  members: `${BASE}/team/members`,
  member: (id: number) => `${BASE}/team/members/${id}`,
  makeManager: (id: number) => `${BASE}/team/members/${id}/make-manager`,
  makeTeamMember: (id: number) => `${BASE}/team/members/${id}/make-team-member`,
  signInEmail: (id: number) => `${BASE}/team/members/${id}/sign-in-email`,
  offers: `${BASE}/team/offers`,
  giveAccess: (offerId: number) => `${BASE}/team/offers/${offerId}/give-access`,
  share: (offerId: number) => `${BASE}/team/offers/${offerId}/share`,
  setAside: (offerId: number) => `${BASE}/team/offers/${offerId}/set-aside`,
  startingSets: `${BASE}/team/starting-sets`,
  startingSet: (code: string) => `${BASE}/team/starting-sets/${code}`,
  leave: `${BASE}/me/leave`,
  accounts: `${BASE}/me/accounts`,
  switchAccount: `${BASE}/me/switch`,
  handOver: `${BASE}/ownership/handover`,
  handOverOne: (id: number) => `${BASE}/ownership/handover/${id}`,
} as const;

/* ---------- types ---------- */

export type MemberRole = AccessRole;
export type MemberStatus = { kind: "active" } | { kind: "not_signed_in" } | { kind: "ends"; date: string } | { kind: "ended"; date: string };

/** What a person can see: everything, nothing, or the counts of a chosen reach (`contacts` counts the families on the routes given too). */
export type CanSee = {
  all?: boolean;
  nothing?: boolean;
  contacts?: number;
  categories?: number;
  picked?: number;
  routes?: number;
  families_on_routes?: number;
};

/** Switches in `switches_on` are permission keys (`routes.follow`, `contacts.edit`, …); details and history have their own flags. */
export type CanDo = { everything: boolean; switches_on: string[]; details: boolean; history: boolean };

/** One row of the members list, as the server sends it (B1). */
export type MemberRow = {
  user_id: number;
  name: string;
  email: string;
  note: string | null;
  role: MemberRole;
  you: boolean;
  owner: boolean;
  preset: string | null;
  preset_unchanged: boolean;
  /** A6: the role he follows (null = Personal settings) and whether any switch is only his. */
  account_role?: { id: number; name: string } | null;
  own_settings?: boolean;
  can_see: CanSee;
  can_do: CanDo;
  status: MemberStatus;
  last_active: string | null;
  can: { edit: boolean; send_link: boolean; remove: boolean; make_manager: boolean; make_team_member: boolean; hand_over: boolean };
};

export type TeamSegment = "all" | "managers" | "team_members" | "not_signed_in" | "ended";
export type TeamListParams = { q?: string; segment?: TeamSegment; page?: number; perPage?: number };
export type TeamList = {
  rows: MemberRow[];
  meta: {
    total: number;
    page: number;
    per_page: number;
    counts: { members: number; not_signed_in: number; offers: number };
    can: { invite: boolean; see_offers: boolean; make_managers: boolean };
    view_only: boolean;
  };
};

export type ReachDetail = {
  all: boolean;
  categories: { id: number; name: string; color: string | null; count: number }[];
  contacts: { id: number; name: string; address_line: string | null }[];
  routes: { id: number; name: string; project_name: string | null; stops: number }[];
};

export type HistoryEntry = {
  at: string;
  by: { name: string } | null;
  by_staff: { staff_name: string | null } | null;
  fields: string[];
  day_one: boolean;
};

/** MemberRow plus what the member page needs (B3). */
export type MemberDetail = MemberRow & {
  member_since: string | null;
  /** Sent back on save; a stale one is refused with 409 ACCESS_CHANGED. */
  version: string;
  /** Number of switches that differ from the starting set, when the server says. */
  changed_switches?: number;
  state: {
    role: MemberRole;
    preset: string | null;
    /** Effective value of every switch, by permission key. */
    switches: Record<string, boolean>;
    reach: ReachDetail;
    /** A6: the role with what it allows and shows; null = Personal settings. */
    account_role?: { id: number; name: string; switches: Record<string, boolean>; has_reach: boolean; reach: import("./roles/api").SeeFacts | null } | null;
    /** A6: permission keys of the switches set only for this person while linked. */
    overrides?: string[];
    /** A6: false while "what they see" is the role's. */
    own_reach?: boolean;
    ends_at: string | null;
    billing_allowed?: boolean;
  };
  history: HistoryEntry[];
  /** The account's live pending hand-over, only when it names this member as giver or receiver. */
  handover: { id: number; status: string; from: { id: number; name: string }; to: { id: number; name: string }; created_at?: string } | null;
};

/** A save or an add: the wire `MemberAccessChange` in camelCase. An absent key = untouched. */
export type AccessFormValue = {
  role?: "team_member" | "manager";
  preset?: string | null;
  /** A6: link or move to this role (a move resets the person to exactly the role). */
  accountRoleId?: number;
  /** A6: leave the role for Personal settings, nothing changes for the person. */
  personal?: boolean;
  /** A6: use the role's "what they see" again. */
  reachFromRole?: boolean;
  switches?: Record<string, boolean>;
  reach?: { all: boolean; categoryIds: number[]; householdIds: number[]; routeIds: number[] };
  endsAt?: string | null;
  note?: string | null;
  billingAllowed?: boolean;
};

export type AddMemberInput = AccessFormValue & { email: string; firstName: string; lastName: string; role: "team_member" | "manager" };
export type AddMemberResult = { user_id: number; name: string; email_sent: boolean; existing_user: boolean; summary: MemberRow };

export type Offer = {
  id: number;
  name: string;
  email: string;
  phone: string | null;
  /** Option keys, translated on the client. */
  offered: string[];
  offered_on: string;
  shared_by: { name: string } | null;
  can_unshare: boolean;
};
export type Offers = { offers: Offer[]; set_aside: Offer[]; count: number };

export type MyAccount = { id: number; name: string; role: MemberRole | null; account_type: string | null; state: "ok" | "view_only" | "closed"; current: boolean };

/** A starting set (5A): the two standard ones carry `name: null`. */
export type StartingSet = { code: string; name: string | null; standard: boolean; changed: boolean; reach_all: boolean; permissions: string[] };
export type StartingSetInput = { name?: string | null; reachAll: boolean; permissions: string[] };
export type StartingSets = { sets: StartingSet[]; switches: string[] };

export type HandOverStatus = "pending" | "accepted" | "declined" | "cancelled" | string;
export type HandOver = { id: number; status: HandOverStatus; from: { id: number; name: string }; to?: { id: number; name: string }; created_at?: string; workspace_name?: string; workspace_id?: number };

/* ---------- camelCase -> wire ---------- */

type Wire = Record<string, any>;

/** Only the keys that are present go out (an absent key = untouched on the server). */
export const toWireChange = (v: AccessFormValue): Wire => {
  const w: Wire = {};
  if (v.role !== undefined) w.role = v.role;
  if (v.preset !== undefined) w.preset = v.preset;
  if (v.accountRoleId !== undefined) w.account_role_id = v.accountRoleId;
  if (v.personal) w.personal = true;
  if (v.reachFromRole) w.reach_from_role = true;
  if (v.switches !== undefined) w.switches = v.switches;
  if (v.reach !== undefined) w.reach = { all: v.reach.all, category_ids: v.reach.categoryIds, household_ids: v.reach.householdIds, route_ids: v.reach.routeIds };
  if (v.endsAt !== undefined) w.ends_at = v.endsAt;
  if (v.note !== undefined) w.note = v.note;
  if (v.billingAllowed !== undefined) w.billing_allowed = v.billingAllowed;
  return w;
};

const sameIds = (a: number[], b: number[]) => a.length === b.length && [...a].sort((x, y) => x - y).every((x, i) => x === [...b].sort((p, q) => p - q)[i]);

/** What differs between the form as loaded and as it is now: the only keys a save sends. */
export const changedAccess = (before: AccessFormValue, now: AccessFormValue): AccessFormValue => {
  const out: AccessFormValue = {};
  if (now.role !== undefined && now.role !== before.role) out.role = now.role;
  if (now.preset !== undefined && now.preset !== before.preset) out.preset = now.preset;
  if (now.switches) {
    const diff: Record<string, boolean> = {};
    for (const [k, on] of Object.entries(now.switches)) if ((before.switches?.[k] ?? false) !== on) diff[k] = on;
    if (Object.keys(diff).length) out.switches = diff;
  }
  if (now.reach) {
    const b = before.reach;
    if (!b || b.all !== now.reach.all || !sameIds(b.categoryIds, now.reach.categoryIds) || !sameIds(b.householdIds, now.reach.householdIds) || !sameIds(b.routeIds, now.reach.routeIds)) out.reach = now.reach;
  }
  if (now.endsAt !== undefined && now.endsAt !== (before.endsAt ?? null)) out.endsAt = now.endsAt;
  if (now.note !== undefined && now.note !== (before.note ?? null)) out.note = now.note;
  if (now.billingAllowed !== undefined && now.billingAllowed !== before.billingAllowed) out.billingAllowed = now.billingAllowed;
  return out;
};

const toWireAdd = (v: AddMemberInput): Wire => ({ email: v.email, first_name: v.firstName, last_name: v.lastName, ...toWireChange(v) });

const listQuery = (p: TeamListParams): Record<string, string | number> => {
  const q: Record<string, string | number> = {};
  if (p.q?.trim()) q.q = p.q.trim();
  if (p.segment && p.segment !== "all") q.segment = p.segment;
  if (p.page && p.page > 1) q.page = p.page;
  if (p.perPage) q.per_page = p.perPage;
  return q;
};

const setFromWire = (w: Wire): StartingSet => ({ code: w.code, name: w.name ?? null, standard: !!w.standard, changed: !!w.changed, reach_all: !!w.reach_all, permissions: w.permissions ?? [] });
const setToWire = (v: StartingSetInput): Wire => ({ ...(v.name !== undefined ? { name: v.name } : {}), reach_all: v.reachAll, permissions: v.permissions });

/* ---------- reads ---------- */

export const useTeamMembers = (params: TeamListParams = {}) => {
  const ws = useWorkspaceId();
  const query = listQuery(params);
  return useQuery({
    queryKey: scopedKey(ws, "team", "members", query),
    queryFn: async (): Promise<TeamList> => {
      const w = await api.get<{ data: MemberRow[]; meta: TeamList["meta"] }>(TEAM_URLS.members, query);
      return { rows: w.data, meta: w.meta };
    },
    placeholderData: keepPreviousData,
    enabled: ws !== null,
  });
};

/** `segment=all`: a person whose access ended still opens (without it the server answers 404 MEMBER_NOT_FOUND). */
export const useMember = (userId: number | null) => {
  const ws = useWorkspaceId();
  return useQuery({
    queryKey: scopedKey(ws, "team", "member", userId),
    queryFn: async () => (await api.get<{ data: MemberDetail }>(TEAM_URLS.member(userId as number), { segment: "all" })).data,
    enabled: ws !== null && userId !== null,
  });
};

export const useOffers = (opts: { enabled?: boolean } = {}) => {
  const ws = useWorkspaceId();
  return useQuery({
    queryKey: scopedKey(ws, "team", "offers"),
    queryFn: async (): Promise<Offers> => {
      const w = await api.get<{ data: { offers: Offer[]; set_aside: Offer[] }; meta?: { count?: number } }>(TEAM_URLS.offers);
      return { offers: w.data.offers, set_aside: w.data.set_aside, count: w.meta?.count ?? w.data.offers.length };
    },
    enabled: ws !== null && (opts.enabled ?? true),
  });
};

export const useMyAccounts = () => {
  const ws = useWorkspaceId();
  return useQuery({
    queryKey: scopedKey(ws, "team", "my-accounts"),
    queryFn: async () => (await api.get<{ data: MyAccount[] }>(TEAM_URLS.accounts)).data,
    enabled: ws !== null,
  });
};

export const useStartingSets = (opts: { enabled?: boolean } = {}) => {
  const ws = useWorkspaceId();
  return useQuery({
    queryKey: scopedKey(ws, "team", "starting-sets"),
    queryFn: async (): Promise<StartingSets> => {
      const w = await api.get<{ data: Wire[]; switches: string[] }>(TEAM_URLS.startingSets);
      return { sets: w.data.map(setFromWire), switches: w.switches ?? [] };
    },
    enabled: ws !== null && opts.enabled !== false,
  });
};

/** The account's starting sets by code, for words like "Started from …"; undefined until known (or when not asked for). */
export const useSetNames = (enabled = true): Record<string, string> | undefined => {
  const q = useStartingSets({ enabled });
  return useMemo(() => {
    if (!q.data) return undefined;
    const out: Record<string, string> = {};
    for (const x of q.data.sets) if (x.name) out[x.code] = x.name;
    return out;
  }, [q.data]);
};

/** The accept screen of a hand-over. */
export const useHandOver = (id: number | null) => {
  const ws = useWorkspaceId();
  return useQuery({
    queryKey: scopedKey(ws, "team", "handover", id),
    queryFn: async () => (await api.get<{ data: HandOver }>(TEAM_URLS.handOverOne(id as number))).data,
    enabled: ws !== null && id !== null,
  });
};

/* ---------- writes: each refreshes the team lists and (their own access may have changed) the access answer ---------- */

const useRefreshTeam = () => {
  const ws = useWorkspaceId();
  const client = useQueryClient();
  return () => Promise.all([client.invalidateQueries({ queryKey: ["ws", ws, "team"] }), client.invalidateQueries({ queryKey: ["ws", ws, "access"] })]);
};

/** Adds a person (creates the login when there is none, gives access at once, e-mails how to sign in). */
export const useAddMember = () => {
  const refresh = useRefreshTeam();
  return useMutation({
    mutationFn: async (input: AddMemberInput) => (await api.post<{ data: AddMemberResult }>(TEAM_URLS.members, toWireAdd(input))).data,
    onSuccess: refresh,
  });
};

/** `change` holds only what changed (see `changedAccess`); `version` is the one the page loaded. */
export const useSaveMember = (userId: number) => {
  const refresh = useRefreshTeam();
  return useMutation({
    mutationFn: async (input: { change: AccessFormValue; version: string }) =>
      (await api.patch<{ data: MemberDetail; summary_line: { contacts: number; routes: number } }>(TEAM_URLS.member(userId), { change: toWireChange(input.change), version: input.version })),
    onSuccess: refresh,
  });
};

export const useMakeManager = (userId: number) => {
  const refresh = useRefreshTeam();
  return useMutation({ mutationFn: async () => (await api.post<{ data: MemberDetail }>(TEAM_URLS.makeManager(userId), {})).data, onSuccess: refresh });
};

export const useMakeTeamMember = (userId: number) => {
  const refresh = useRefreshTeam();
  return useMutation({ mutationFn: async () => (await api.post<{ data: MemberDetail }>(TEAM_URLS.makeTeamMember(userId), {})).data, onSuccess: refresh });
};

/** `confirmName`: the typed name, required by the server when the person removed is a Manager. */
export const useRemoveMember = (userId: number) => {
  const refresh = useRefreshTeam();
  return useMutation({
    mutationFn: async (input: { confirmName?: string } = {}) => {
      await api.del(TEAM_URLS.member(userId), input.confirmName !== undefined ? { confirm_name: input.confirmName } : undefined);
    },
    onSuccess: refresh,
  });
};

/** Sends the sign-in e-mail again; only for people who have not signed in yet. */
export const useSendSignInLink = (userId: number) =>
  useMutation({ mutationFn: async () => { await api.post(TEAM_URLS.signInEmail(userId), {}); } });

export const useGiveAccess = (offerId: number) => {
  const refresh = useRefreshTeam();
  return useMutation({
    mutationFn: async (input: AccessFormValue & { role: "team_member" | "manager" }) => (await api.post<{ data: AddMemberResult }>(TEAM_URLS.giveAccess(offerId), toWireChange(input))).data,
    onSuccess: refresh,
  });
};

export const useShareOffer = (offerId: number) => {
  const refresh = useRefreshTeam();
  return useMutation({ mutationFn: async (shliachUserId: number) => { await api.post(TEAM_URLS.share(offerId), { shliach_user_id: shliachUserId }); }, onSuccess: refresh });
};

/** `aside` true sets the offer aside, false brings it back. */
export const useSetAside = (offerId: number, aside: boolean) => {
  const refresh = useRefreshTeam();
  return useMutation({
    mutationFn: async () => { await (aside ? api.post(TEAM_URLS.setAside(offerId), {}) : api.del(TEAM_URLS.setAside(offerId))); },
    onSuccess: refresh,
  });
};

/** Leave an account: the one given (My settings lists them all), else the one you are in. Answers the account to open next, or null. */
export const useLeave = () => {
  const refresh = useRefreshTeam();
  return useMutation({
    mutationFn: async (workspaceId?: number) =>
      (await api.post<{ data: { next_workspace_id: number | null; ask_delete_offers?: Array<{ link_id: number; center_name: string | null; workspace_name: string | null }> } }>(TEAM_URLS.leave, workspaceId === undefined ? {} : { workspace_id: workspaceId })).data,
    onSuccess: refresh,
  });
};

export const useSwitchAccount = () => {
  const ws = useWorkspaceId();
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (workspaceId: number) => { await api.post(TEAM_URLS.switchAccount, { workspace_id: workspaceId }); },
    onSuccess: () => Promise.all([client.invalidateQueries({ queryKey: ["ws", ws, "team", "my-accounts"] }), client.invalidateQueries({ queryKey: ["ws", ws, "access"] })]),
  });
};

/* ---------- starting sets ---------- */

/** One hook for any set: the screen saves several in a row, so the code travels with the input. */
export const useSaveStartingSet = () => {
  const refresh = useRefreshTeam();
  return useMutation({
    mutationFn: async ({ code, ...input }: StartingSetInput & { code: string }) => setFromWire((await api.put<{ data: Wire }>(TEAM_URLS.startingSet(code), setToWire(input))).data),
    onSuccess: refresh,
  });
};

export const useCreateStartingSet = () => {
  const refresh = useRefreshTeam();
  return useMutation({ mutationFn: async (input: StartingSetInput & { name: string }) => setFromWire((await api.post<{ data: Wire }>(TEAM_URLS.startingSets, setToWire(input))).data), onSuccess: refresh });
};

/** A named set goes; a standard one is reset to the standard switches. */
export const useDeleteStartingSet = () => {
  const refresh = useRefreshTeam();
  return useMutation({ mutationFn: async (code: string) => { await api.del(TEAM_URLS.startingSet(code)); }, onSuccess: refresh });
};

/* ---------- hand-over (5A endpoints) ---------- */

export const useStartHandOver = () => {
  const refresh = useRefreshTeam();
  return useMutation({
    mutationFn: async (input: { toUserId: number; confirmName: string }) => (await api.post<{ data: HandOver }>(TEAM_URLS.handOver, { to_user_id: input.toUserId, confirm_name: input.confirmName })).data,
    onSuccess: refresh,
  });
};

export const useCancelHandOver = () => {
  const refresh = useRefreshTeam();
  return useMutation({ mutationFn: async (id: number) => { await api.del(TEAM_URLS.handOverOne(id)); }, onSuccess: refresh });
};

export const useAcceptHandOver = () => {
  const refresh = useRefreshTeam();
  return useMutation({
    mutationFn: async (input: { id: number; token?: string }) => (await api.post<{ data: HandOver }>(`${TEAM_URLS.handOverOne(input.id)}/accept`, input.token ? { token: input.token } : {})).data,
    onSuccess: refresh,
  });
};
